import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react'
import type { ApisauceInstance } from 'apisauce'
import type {
  MediaTokenListener,
  MediaTokenStore,
  MediasConfig,
} from './types'
import { MediasAPI } from './api'

export type UseResolvedUrlResult = {
  src: string
  onError: () => void
  isCdnMedia: boolean
}

export class MediasPackage {
  public API: MediasAPI

  private readonly tokenStore: MediaTokenStore
  private readonly api: ApisauceInstance
  private readonly resolveCdnBaseUrl: () => string
  private getLockEnabled: () => boolean = () => false

  private cachedToken: string | null = null
  private tokenLoaded = false
  private inflight: Promise<string> | null = null
  private listeners: Set<MediaTokenListener> = new Set()

  constructor(config: MediasConfig) {
    const cdn = config.cdnBaseUrl
    this.resolveCdnBaseUrl = typeof cdn === 'function' ? cdn : () => cdn
    this.tokenStore = config.tokenStore
    this.api = config.api
    this.API = new MediasAPI(config.api)
  }

  get cdnBaseUrl(): string {
    return this.resolveCdnBaseUrl().replace(/\/$/, '')
  }

  get mediaPathPrefix(): string {
    return `${this.api.getBaseURL().replace(/\/$/, '')}/profiles/file/`
  }

  getToken(): string | null {
    if (!this.tokenLoaded) {
      this.cachedToken = this.tokenStore.get()
      this.tokenLoaded = true
    }
    return this.cachedToken
  }

  hasToken(): boolean {
    return this.getToken() !== null
  }

  setToken(token: string): void {
    this.cachedToken = token
    this.tokenLoaded = true
    this.tokenStore.set(token)
    this.notify()
  }

  clearToken(): void {
    this.cachedToken = null
    this.tokenLoaded = true
    this.inflight = null
    this.tokenStore.remove()
    this.notify()
  }

  async ensureToken(): Promise<string> {
    const cached = this.getToken()
    if (cached) return cached
    return this.fetchTokenInternal()
  }

  async refreshToken(): Promise<string> {
    return this.fetchTokenInternal()
  }

  setLockEnabledGetter(fn: () => boolean): void {
    this.getLockEnabled = fn
  }

  resolveUrl(url: string | null | undefined): string {
    if (!url) return ''
    if (!this.getLockEnabled()) return url

    const key = this.extractKey(url)
    if (key === null) return url

    const token = this.getToken()
    if (!token) return ''

    return this.buildProxyUrl(key, token)
  }

  extractKey(url: string): string | null {
    if (!url) return null

    if (url.startsWith(this.cdnBaseUrl)) {
      const remainder = url.slice(this.cdnBaseUrl.length)
      const path = remainder.startsWith('/') ? remainder.slice(1) : remainder
      const key = path.split('?')[0]
      return key.startsWith('media/public/') ? key.slice('media/public/'.length) : key
    }

    const proxyPrefix = `${this.mediaPathPrefix}?`
    if (url.startsWith(proxyPrefix)) {
      const queryString = url.slice(proxyPrefix.length)
      const params = new URLSearchParams(queryString)
      return params.get('key')
    }

    return null
  }

  isMediaUrl(url: string | null | undefined): boolean {
    if (!url) return false
    return this.extractKey(url) !== null
  }

  private buildProxyUrl(key: string, token: string): string {
    return `${this.mediaPathPrefix}?key=${encodeURIComponent(key)}&token=${encodeURIComponent(token)}`
  }

  useBootstrapper(isLoggedIn: boolean): void {
    // useEffect required: ensureToken is an imperative async call that must fire
    // exactly once when auth resolves, and it has no natural integration as a
    // derived value. clearToken on logout happens in the session utils.
    useEffect(() => {
      if (!isLoggedIn) return
      if (this.hasToken()) return
      this.ensureToken().catch(() => {
        // Failure leaves cache empty; next image onError will trigger refreshToken.
      })
    }, [isLoggedIn])
  }

  /**
   * Subscribes to the cached media token. The component re-renders whenever
   * the token is set, refreshed, or cleared.
   *
   * Most call sites should prefer {@link useResolvedUrl}. Reach for
   * `useToken` directly only when you need to call the synchronous
   * {@link resolveUrl} inside a loop in the same component (e.g.
   * `users.map(...)`) and still have the parent re-render on token
   * rotation.
   */
  useToken(): string | null {
    return useSyncExternalStore(
      (onChange) => this.subscribe(() => onChange()),
      () => this.getToken(),
      () => this.getToken(),
    )
  }

  /**
   * Returns everything a consumer needs to display a media URL:
   *
   * - `src` — resolved URL (proxy URL for CDN-owned media, raw URL for
   *   third-party, `''` when no token is cached yet). Re-renders whenever
   *   the cached token is set, rotated, or cleared.
   * - `onError` — wire to your renderer's `onError`. On the first failure
   *   for a given image it triggers exactly one {@link refreshToken} call;
   *   repeated errors for the same URL are no-ops.
   * - `isCdnMedia` — `true` when `url` belongs to the proxy's CDN. Use this
   *   to gate whether to attach `onError` at all (no point retrying for
   *   third-party URLs).
   *
   * String-only consumers (e.g. passing `avatar` to a third-party
   * `AvatarInfo` / `ProfileBanner` component) should destructure just
   * `src`: `const { src: avatar } = Medias.useResolvedUrl(user.image)`.
   *
   * For iterating a list in a single component, prefer {@link useToken}
   * once at the top + the imperative {@link resolveUrl} inside the loop.
   */
  useResolvedUrl(url: string | null | undefined): UseResolvedUrlResult {
    this.useToken()

    const retryCountRef = useRef(0)
    const lastUrlRef = useRef<string | null | undefined>(url)

    if (lastUrlRef.current !== url) {
      lastUrlRef.current = url
      retryCountRef.current = 0
    }

    const isCdnMedia = this.getLockEnabled() && this.isMediaUrl(url)
    const src = this.resolveUrl(url)

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const onError = useCallback(() => {
      if (!isCdnMedia) return
      if (retryCountRef.current > 0) return
      retryCountRef.current += 1
      this.refreshToken().catch(() => {})
    }, [isCdnMedia])

    return { src, onError, isCdnMedia }
  }

  subscribe(listener: MediaTokenListener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private fetchTokenInternal(): Promise<string> {
    if (this.inflight) return this.inflight

    const promise = this.API.fetchToken()
      .then((token) => {
        this.cachedToken = token
        this.tokenLoaded = true
        this.tokenStore.set(token)
        this.inflight = null
        this.notify()
        return token
      })
      .catch((err) => {
        this.inflight = null
        throw err
      })

    this.inflight = promise
    return promise
  }

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.cachedToken)
    }
  }
}

export type { MediasConfig, MediaTokenStore, MediaTokenListener } from './types'
